import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { FormLibraryForm } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Edit2, FileText, Library, Eye } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface PlanFormAssociation {
  id: number;
  planId: number;
  formId: number;
  isRequired?: boolean;
  showForCoverageTiers?: string[];
  form?: FormLibraryForm & { fields?: any[] };
  createdAt?: string;
  updatedAt?: string;
}

const COVERAGE_TIERS = [
  { value: "employee_only", label: "Employee Only" },
  { value: "employee_spouse", label: "Employee + Spouse" },
  { value: "employee_children", label: "Employee + Children" },
  { value: "family", label: "Family" },
];

const FORM_TYPE_LABELS: Record<string, string> = {
  application: "Application",
  enrollment: "Enrollment",
  change: "Change",
  waiver: "Waiver",
  sbc: "SBC",
  eoc: "EOC",
  form: "Form",
  other: "Other",
};

interface CarrierFormItem {
  id: number;
  name: string;
  carrierId: number | null;
  formType: string;
  carrierName: string | null;
  source: "carrier_library";
}

interface PickerForm {
  id: number;
  name: string;
  formType: string;
  description?: string | null;
  carrierId?: number | null;
  carrierName?: string | null;
  source: "broker_library" | "carrier_library";
}

export function PlanFormLibraryPanel({ planId, carrierId }: { planId: number; carrierId?: number | null }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedForm, setSelectedForm] = useState<PickerForm | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingAssociation, setEditingAssociation] =
    useState<PlanFormAssociation | null>(null);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [previewForm, setPreviewForm] = useState<{ url: string; name: string } | null>(null);

  const [formData, setFormData] = useState({
    isRequired: true,
    showForCoverageTiers: [] as string[],
  });

  // Fetch forms associated with this plan
  const { data: associations = [], refetch } = useQuery<PlanFormAssociation[]>({
    queryKey: [`/api/plans/${planId}/forms`],
  });

  // Fetch broker forms from the library
  const { data: allForms = [] } = useQuery<FormLibraryForm[]>({
    queryKey: ["/api/forms"],
  });

  // Fetch carrier forms (superadmin forms available to all brokers)
  const { data: carrierFormsList = [] } = useQuery<CarrierFormItem[]>({
    queryKey: ["/api/carrier-forms"],
  });

  // Create association
  const createMutation = useMutation({
    mutationFn: async () => {
      if (!selectedForm) throw new Error("No form selected");
      const body: any = {
        isRequired: formData.isRequired,
        showForCoverageTiers: formData.showForCoverageTiers,
      };
      if (selectedForm.source === "carrier_library") {
        body.carrierFormId = selectedForm.id;
      } else {
        body.formId = selectedForm.id;
      }
      return apiRequest("POST", `/api/plans/${planId}/forms`, body);
    },
    onSuccess: () => {
      toast({ title: "Form added to plan" });
      refetch();
      resetForm();
      setIsCreateDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to add form",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update association
  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingAssociation) throw new Error("No association selected");
      return apiRequest(
        "PUT",
        `/api/plan-forms/${editingAssociation.id}`,
        formData
      );
    },
    onSuccess: () => {
      toast({ title: "Form association updated" });
      refetch();
      resetForm();
      setIsCreateDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete association
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/plan-forms/${id}`);
    },
    onSuccess: () => {
      toast({ title: "Form removed from plan" });
      refetch();
      setDeleteAlertOpen(false);
      setDeleteId(null);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to remove form",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Merge broker forms + carrier forms into a single picker list, split by carrier match
  const { recommendedForms, otherForms } = useMemo(() => {
    const associatedFormIds = new Set(associations.filter((a: any) => a.formId).map((a: any) => a.formId));
    const associatedCarrierFormIds = new Set(associations.filter((a: any) => a.carrierFormId).map((a: any) => a.carrierFormId));
    const q = searchValue.trim().toLowerCase();

    // Broker forms
    const brokerItems: PickerForm[] = allForms
      .filter((f) => f.status === "active" && !associatedFormIds.has(f.id))
      .filter((f) =>
        !q || f.name.toLowerCase().includes(q) ||
        (f.formType && f.formType.toLowerCase().includes(q)) ||
        (f.description && f.description.toLowerCase().includes(q))
      )
      .map((f) => ({ id: f.id, name: f.name, formType: f.formType || "", description: f.description, carrierId: (f as any).carrierId, source: "broker_library" as const }));

    // Carrier forms (superadmin)
    const carrierItems: PickerForm[] = carrierFormsList
      .filter((f) => !associatedCarrierFormIds.has(f.id))
      .filter((f) =>
        !q || f.name.toLowerCase().includes(q) ||
        (f.formType && f.formType.toLowerCase().includes(q)) ||
        (f.carrierName && f.carrierName.toLowerCase().includes(q))
      )
      .map((f) => ({ id: f.id, name: f.name, formType: f.formType, carrierName: f.carrierName, carrierId: f.carrierId, source: "carrier_library" as const }));

    const all = [...brokerItems, ...carrierItems];

    if (!carrierId) return { recommendedForms: [] as PickerForm[], otherForms: all };

    const recommended: PickerForm[] = [];
    const other: PickerForm[] = [];
    for (const f of all) {
      if (f.carrierId === carrierId) {
        recommended.push(f);
      } else {
        other.push(f);
      }
    }
    return { recommendedForms: recommended, otherForms: other };
  }, [allForms, carrierFormsList, associations, searchValue, carrierId]);

  const openPreview = (source: "carrier_library" | "broker_library", id: number, documentId?: number, name?: string) => {
    const url = source === "carrier_library"
      ? `/api/carrier-forms/${id}/content`
      : documentId ? `/api/documents/${documentId}/content?disposition=inline` : null;
    if (url) setPreviewForm({ url, name: name || "Form Preview" });
  };

  const handleCreate = () => {
    resetForm();
    setSelectedForm(null);
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (association: PlanFormAssociation) => {
    setEditingAssociation(association);
    const f = association.form as any;
    setSelectedForm(f ? { id: f.id, name: f.name, formType: f.formType || "", source: f.source || "broker_library" } : null);
    setFormData({
      isRequired: association.isRequired ?? true,
      showForCoverageTiers: association.showForCoverageTiers || [],
    });
    setIsCreateDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setDeleteAlertOpen(true);
  };

  const resetForm = () => {
    setFormData({
      isRequired: true,
      showForCoverageTiers: [],
    });
    setEditingAssociation(null);
    setSelectedForm(null);
    setSearchValue("");
  };

  const handleSubmit = () => {
    if (!selectedForm && !editingAssociation) {
      toast({ title: "Please select a form", variant: "destructive" });
      return;
    }
    if (editingAssociation) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium flex items-center gap-2">
            <Library className="h-4 w-4" />
            Form Library
          </h4>
          <p className="text-sm text-muted-foreground">
            Attach forms from the library to show during enrollment
          </p>
        </div>
        <Button onClick={handleCreate} variant="outline" size="sm" className="gap-2">
          <Plus className="w-4 h-4" />
          Add Form
        </Button>
      </div>

      {/* Recommended carrier forms (shown even when no forms attached yet) */}
      {associations.length === 0 && recommendedForms.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4 space-y-3">
          <p className="text-sm font-medium text-blue-800">
            Recommended forms for this carrier
          </p>
          {recommendedForms.map((form) => (
            <div key={`${form.source}-${form.id}`} className="flex items-center justify-between bg-white rounded-md border px-3 py-2">
              <div>
                <p className="text-sm font-medium">{form.name}</p>
                <p className="text-xs text-muted-foreground">
                  {FORM_TYPE_LABELS[form.formType || ""] || form.formType || "—"}
                  {form.carrierName && ` · ${form.carrierName}`}
                  {form.source === "carrier_library" && " · Carrier Library"}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => {
                  setSelectedForm(form);
                  setFormData({ isRequired: true, showForCoverageTiers: [] });
                  setEditingAssociation(null);
                  setIsCreateDialogOpen(true);
                }}
              >
                <Plus className="w-3 h-3" />
                Add
              </Button>
            </div>
          ))}
        </div>
      )}

      {associations.length === 0 ? (
        <div className="text-center py-6 rounded-lg border border-dashed">
          <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            No forms from the library attached yet
          </p>
          <Button
            onClick={handleCreate}
            variant="outline"
            size="sm"
            className="mt-3"
          >
            {recommendedForms.length > 0 ? "Browse All Forms" : "Add First Form"}
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Form Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Show During Enrollment</TableHead>
                <TableHead>Coverage Tiers</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {associations.map((assoc) => (
                <TableRow key={assoc.id}>
                  <TableCell>
                    <div className="font-medium">
                      {assoc.form?.name || `Form #${assoc.formId}`}
                    </div>
                    {assoc.form?.description && (
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {assoc.form.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {FORM_TYPE_LABELS[assoc.form?.formType || ""] || assoc.form?.formType || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={assoc.isRequired !== false ? "default" : "secondary"}>
                      {assoc.isRequired !== false ? "Shown" : "Hidden"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {assoc.showForCoverageTiers &&
                    assoc.showForCoverageTiers.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {assoc.showForCoverageTiers.map((tier) => (
                          <Badge key={tier} variant="outline" className="text-xs">
                            {COVERAGE_TIERS.find((t) => t.value === tier)?.label ?? tier}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">All tiers</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const f = assoc.form as any;
                          const source = f?.source || ((assoc as any).carrierFormId ? "carrier_library" : "broker_library");
                          const formId = source === "carrier_library" ? (assoc as any).carrierFormId : assoc.formId;
                          openPreview(source, formId, f?.documentId, f?.name);
                        }}
                        className="gap-1"
                        title="Preview PDF"
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(assoc)}
                        className="gap-1"
                      >
                        <Edit2 className="w-3 h-3" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(assoc.id)}
                        className="gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAssociation ? "Edit Form Settings" : "Add Form from Library"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Form Selection */}
            <div>
              <label className="text-sm font-medium">Form *</label>
              <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    disabled={!!editingAssociation}
                  >
                    {selectedForm?.name || "Select a form from the library..."}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search forms..."
                      value={searchValue}
                      onValueChange={setSearchValue}
                    />
                    <CommandEmpty>No forms found.</CommandEmpty>
                    <CommandList>
                      {recommendedForms.length > 0 && (
                        <CommandGroup heading="Recommended (same carrier)">
                          {recommendedForms.map((form) => {
                            const key = `${form.source}-${form.id}`;
                            const isSelected = selectedForm?.id === form.id && selectedForm?.source === form.source;
                            return (
                              <CommandItem
                                key={key}
                                value={key}
                                onSelect={() => { setSelectedForm(form); setSearchOpen(false); }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                                <div className="flex flex-col flex-1">
                                  <span>{form.name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {FORM_TYPE_LABELS[form.formType || ""] || form.formType || "—"}
                                    {form.carrierName && ` · ${form.carrierName}`}
                                    {form.source === "carrier_library" && " · Carrier Library"}
                                  </span>
                                </div>
                                <Eye
                                  className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground shrink-0"
                                  onClick={(e) => { e.stopPropagation(); openPreview(form.source, form.id, undefined, form.name); }}
                                />
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      )}
                      <CommandGroup heading={recommendedForms.length > 0 ? "Other forms" : undefined}>
                        {otherForms.map((form) => {
                          const key = `${form.source}-${form.id}`;
                          const isSelected = selectedForm?.id === form.id && selectedForm?.source === form.source;
                          return (
                            <CommandItem
                              key={key}
                              value={key}
                              onSelect={() => { setSelectedForm(form); setSearchOpen(false); }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                              <div className="flex flex-col flex-1">
                                <span>{form.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {FORM_TYPE_LABELS[form.formType || ""] || form.formType || "—"}
                                  {form.carrierName && ` · ${form.carrierName}`}
                                  {form.source === "carrier_library" && " · Carrier Library"}
                                </span>
                              </div>
                              <Eye
                                className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground shrink-0"
                                onClick={(e) => { e.stopPropagation(); openPreview(form.source, form.id, undefined, form.name); }}
                              />
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Show during enrollment */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="plan-form-required"
                  checked={formData.isRequired}
                  onCheckedChange={(checked) =>
                    setFormData((p) => ({
                      ...p,
                      isRequired: checked === true,
                    }))
                  }
                />
                <label htmlFor="plan-form-required" className="text-sm font-medium">
                  Show during enrollment
                </label>
              </div>
            </div>

            {/* Coverage Tiers */}
            <div>
              <label className="text-sm font-medium">Coverage Tiers</label>
              <div className="space-y-2 mt-1">
                {COVERAGE_TIERS.map((tier) => (
                  <div key={tier.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`plan-form-tier-${tier.value}`}
                      checked={formData.showForCoverageTiers.includes(tier.value)}
                      onCheckedChange={(checked) =>
                        setFormData((p) => ({
                          ...p,
                          showForCoverageTiers:
                            checked === true
                              ? Array.from(new Set([...p.showForCoverageTiers, tier.value]))
                              : p.showForCoverageTiers.filter((t) => t !== tier.value),
                        }))
                      }
                    />
                    <label htmlFor={`plan-form-tier-${tier.value}`} className="text-sm">
                      {tier.label}
                    </label>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  Leave empty to show for all coverage tiers
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                createMutation.isPending ||
                updateMutation.isPending ||
                (!selectedForm && !editingAssociation)
              }
            >
              {editingAssociation ? "Update" : "Add Form"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Alert */}
      <AlertDialog open={deleteAlertOpen} onOpenChange={setDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this form from the plan? It will
              no longer appear during enrollment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-2 justify-end">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) deleteMutation.mutate(deleteId);
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive"
            >
              Remove
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Preview Modal */}
      <Dialog open={!!previewForm} onOpenChange={(open) => { if (!open) setPreviewForm(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{previewForm?.name || "Form Preview"}</DialogTitle>
          </DialogHeader>
          {previewForm && (
            <iframe
              src={previewForm.url}
              className="w-full rounded border"
              style={{ height: "70vh" }}
              title="Form PDF Preview"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
